import { CellContext } from '@tanstack/react-table';
import { useStore } from 'zustand';
import { Trash2, Users, XCircle } from 'lucide-react';

import {
  EnvironmentId,
  EnvironmentStatus,
  K8sDistributionType,
} from '@/react/portainer/environments/types';
import { useCancelOmniProvisioning } from '@/react/kubernetes/cluster/omni/queries/useCancelOmniProvisioning';
import { notifySuccess } from '@/portainer/services/notifications';
import { environmentStore } from '@/react/hooks/current-environment-store';

import { Button } from '@@/buttons';
import { Link } from '@@/Link';

import { EnvironmentListItem } from '../types';
import { confirmCancelProvisioning } from '../ConfirmCancelProvisioningModal';
import { confirmDeleteEnvironments } from '../ConfirmDeleteEnvironmentsModal';
import { useDeleteEnvironmentsMutation } from '../useDeleteEnvironmentsMutation';

import { columnHelper } from './helper';

export const actions = columnHelper.display({
  header: 'Actions',
  cell: Cell,
});

function Cell({
  row: { original: environment },
}: CellContext<EnvironmentListItem, unknown>) {
  const credentialId = environment.CloudProvider?.CredentialID;

  if (
    environment.Status === EnvironmentStatus.Provisioning &&
    environment.CloudProvider?.Provider === K8sDistributionType.OMNI &&
    credentialId
  ) {
    return (
      <CancelProvisioningButton
        environmentId={environment.Id}
        environmentName={environment.Name}
        credentialId={credentialId}
      />
    );
  }

  if (environment.Status === EnvironmentStatus.Provisioning) {
    return <>-</>;
  }

  if (environment.Status === EnvironmentStatus.Error) {
    return <RemoveEnvironmentButton environment={environment} />;
  }

  return (
    <Button
      as={Link}
      props={{
        to: 'portainer.endpoints.endpoint.access',
        params: { id: environment.Id },
      }}
      color="link"
      icon={Users}
      data-cy={`environment-manage-access-button-${environment.Name}`}
    >
      Manage access
    </Button>
  );
}

function CancelProvisioningButton({
  environmentId,
  environmentName,
  credentialId,
}: {
  environmentId: EnvironmentId;
  environmentName: string;
  credentialId: number;
}) {
  const cancelMutation = useCancelOmniProvisioning();

  return (
    <Button
      color="dangerlight"
      icon={XCircle}
      onClick={handleCancel}
      disabled={cancelMutation.isLoading}
      data-cy={`environment-cancel-provisioning-button-${environmentName}`}
    >
      Cancel
    </Button>
  );

  async function handleCancel() {
    const { deleteCluster } =
      (await confirmCancelProvisioning(environmentName)) || {};

    if (deleteCluster === undefined) {
      return;
    }

    cancelMutation.mutate(
      { credentialId, environmentId, deleteCluster },
      {
        onSuccess() {
          notifySuccess('Provisioning cancelled', environmentName);
        },
      }
    );
  }
}

function RemoveEnvironmentButton({
  environment,
}: {
  environment: EnvironmentListItem;
}) {
  const currentEnvironmentStore = useStore(environmentStore);
  const deletionMutation = useDeleteEnvironmentsMutation();

  return (
    <Button
      color="dangerlight"
      icon={Trash2}
      onClick={handleRemove}
      disabled={deletionMutation.isLoading}
      data-cy={`environment-remove-button-${environment.Name}`}
    >
      Remove
    </Button>
  );

  async function handleRemove() {
    const { confirmed, deleteClusters } =
      (await confirmDeleteEnvironments([environment])) || {};

    if (!confirmed) {
      return;
    }

    if (currentEnvironmentStore.environmentId === environment.Id) {
      currentEnvironmentStore.clear();
    }

    deletionMutation.mutate(
      [
        {
          id: environment.Id,
          deleteCluster: Boolean(environment.CloudProvider) && deleteClusters,
          name: environment.Name,
        },
      ],
      {
        onSuccess() {
          notifySuccess('Environment successfully removed', environment.Name);
        },
      }
    );
  }
}
